import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import * as i0 from '@angular/core';
import { PLATFORM_ID, Injectable, Inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * A service used to access the document and window in a SSR safe way
 */
class NgxWindowService {
    constructor(document, platformId) {
        this.document = document;
        this.platformId = platformId;
        /**
         * Subject to hold the window-width, defaults to 1200 when no window is defined
         */
        this.widthSubject$ = new BehaviorSubject(1200);
        /**
         * Subject to hold the scroll up event
         */
        this.scrollingUpSubject$ = new BehaviorSubject(true);
        /**
         * Subject to hold the current scroll position
         */
        this.currentScrollPositionSubject$ = new BehaviorSubject(0);
        /**
         * Observable to get the window-width, defaults to 1200 when no window is defined
         */
        this.width$ = this.widthSubject$.asObservable();
        /**
         * Observable to track when the scroll has ended
         */
        this.scrollingUp$ = this.scrollingUpSubject$.asObservable();
        /**
         * Observable of the current scroll position after the scroll has ended
         */
        this.currentScrollPosition$ = this.currentScrollPositionSubject$.asObservable();
        /**
         * Current scroll position after the scroll has ended
         */
        this.currentScrollPosition = 0;
        // Iben: Handle the initial setup of the service
        if (this.isBrowser() && this.hasDocument()) {
            // Iben: Set the window using the default view
            this.window = this.document.defaultView;
            // Iben: Listen to the scroll event of the document
            this.document.addEventListener('scroll', this.handleContentScroll.bind(this));
            // Iben: Set the current window width
            this.widthSubject$.next(this.window.innerWidth);
            // Iben: Listen to the resize event and update the window width
            this.window.addEventListener('resize', () => {
                if (this.window.innerWidth && this.widthSubject$.getValue()) {
                    this.widthSubject$.next(this.window.innerWidth);
                }
            });
        }
    }
    /**
     * Scrolls to the provided position of the page
     *
     * @param offset - Offset to which we want to scroll, scrolls to top when no offset is provided
     */
    scrollTo(offset = 0) {
        if (!this.window) {
            return;
        }
        this.window.scrollTo(0, offset);
    }
    /**
     * Returns whether there is a document present
     */
    hasDocument() {
        return !!this.document;
    }
    /**
     * Returns whether the current platform is a browser
     */
    isBrowser() {
        return isPlatformBrowser(this.platformId);
    }
    /**
     * Run a provided function only when we're in the browser and not in a server side rendered application
     *
     * @param action - Function we want to run in the browser
     */
    runInBrowser(action) {
        if (this.isBrowser) {
            return action({ browserWindow: this.window, browserDocument: this.document });
        }
        console.warn('Browser depended function has not run.');
        return undefined;
    }
    /**
     *
     *
     * @private
     * @memberof NgxWindowService
     */
    handleContentScroll() {
        // Iben: Update the scrollUp subject
        if (window.scrollY > this.currentScrollPosition) {
            this.scrollingUpSubject$.next(false);
        }
        else {
            this.scrollingUpSubject$.next(true);
        }
        // Iben: Update the current scroll position
        this.currentScrollPosition = window.scrollY < 0 ? 0 : window.scrollY;
        this.currentScrollPositionSubject$.next(this.currentScrollPosition);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxWindowService, deps: [{ token: DOCUMENT }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxWindowService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxWindowService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }] });

/*
 * This windowMock constant return an object with a selected spy.
 * The reason why the spy is not provided by default is for this mock
 * to work on both Jest and Jasmine based test-suites.
 *
 * example:
 * NgxWindowMock(jasmine.createSpy());
 * */
const NgxWindowMock = (spy) => ({
    addEventListener: spy,
    defaultView: {
        scrollTo: () => null,
        innerWidth: 720,
        addEventListener: spy,
    },
});
/*
 * Provide a spy for your testing framework because the windowMock requires it.
 *
 * The reason why the spy is not provided by default is for this mock
 * to work on both Jest and Jasmine based test-suites.
 *
 * example:
 * NgxWindowServiceMock(jasmine.createSpy(), 1440);
 * */
const NgxWindowServiceMock = (spy, width = 1200) => ({
    width: new BehaviorSubject(width),
    window: NgxWindowMock(spy),
    scrollTo: () => null,
    hasDocument: () => true,
    isBrowser: () => true,
    runInBrowser: (callback) => callback(),
});

/**
 * Method checks if given value has changed
 * @param {SimpleChange} value: value to check
 * @returns {boolean} whether or not if the given input has changed
 */
const simpleChangeHasChanged = (value) => {
    return value && JSON.stringify(value.previousValue) !== JSON.stringify(value.currentValue);
};

/**
 * Generated bundle index. Do not edit.
 */

export { NgxWindowMock, NgxWindowService, NgxWindowServiceMock, simpleChangeHasChanged };
